import { useState } from 'react';
import { X } from 'lucide-react';
import type { DailyEntry, EntryPatch } from '@tally/lib/types';
import { saveEntry } from '../lib/api.js';

const FIELDS: [keyof DailyEntry, string, string][] = [
  ['weight_kg', 'Weight (kg)', 'Withings'],
  ['body_fat_pct', 'Body fat %', 'Withings'],
  ['waist_cm', 'Waist (cm)', 'Tape'],
  ['steps', 'Steps', 'WHOOP'],
  ['whoop_calories', 'WHOOP total kcal', 'WHOOP'],
  ['calories_in', 'Intake kcal', 'Cronometer'],
  ['protein_g', 'Protein (g)', 'Cronometer'],
  ['sleep_hours', 'Sleep (hrs)', 'WHOOP'],
];

export function TodayEditor({
  today,
  onSaved,
  onClose,
}: {
  today: DailyEntry;
  onSaved: (next: DailyEntry) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, number | null>>(
    Object.fromEntries(FIELDS.map(([k]) => [k, (today[k] as number | null) ?? null])),
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const patch: EntryPatch = { date: today.date, ...draft };
      const saved = await saveEntry(patch);
      onSaved(saved);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-6 p-5 bg-stone-900 border border-stone-800 rounded-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-stone-400 font-mono">Edit today · {today.date}</div>
        <button onClick={onClose} className="text-stone-500 hover:text-stone-200"><X size={14} /></button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {FIELDS.map(([key, label, source]) => (
          <div key={key}>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-stone-500 font-mono mb-1">
              {label} <span className="text-stone-600">· {source}</span>
            </label>
            <input
              type="number"
              step="0.1"
              value={draft[key] ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, [key]: e.target.value === '' ? null : +e.target.value })
              }
              className="w-full bg-stone-950 border border-stone-700 px-3 py-2 text-stone-100 font-mono text-sm focus:border-stone-500 focus:outline-none"
            />
          </div>
        ))}
      </div>
      {err && <div className="text-rose-400 text-xs font-mono mt-4">{err}</div>}
      <div className="flex gap-2 mt-4">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-stone-100 text-stone-950 text-xs uppercase tracking-[0.2em] font-mono hover:bg-stone-300 disabled:opacity-50"
        >
          {saving ? 'saving…' : 'save'}
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 border border-stone-700 text-stone-400 text-xs uppercase tracking-[0.2em] font-mono hover:border-stone-500"
        >
          cancel
        </button>
      </div>
    </div>
  );
}
